import React, { useState, useEffect } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { IoBoatOutline, IoClose } from 'react-icons/io5';
import { FaShip, FaUsers, FaRulerCombined, FaMoneyBillWave, FaCheckCircle, FaTags, FaImage, FaInfoCircle, FaCog, FaClock, FaEuroSign, FaUserFriends, FaBan } from 'react-icons/fa'; // Added more icons
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Swal from 'sweetalert2';

// Define yacht types and statuses for dropdowns
const YACHT_TYPES = ['Motor Yacht', 'Sailing Yacht', 'Catamaran', 'Cruiser', 'Rib Boat', 'Caique'];
const YACHT_STATUSES = ['available', 'maintenance', 'unavailable'];
const CRUISE_TYPES = ['Private Half Day', 'Private Full Day', 'Semi-Private Half Day', 'Semi-Private Full Day'];

// Default durations (can be adjusted or made dynamic if needed)
const DEFAULT_DURATIONS = {
    'Private Half Day': 4,
    'Private Full Day': 8,
    'Semi-Private Half Day': 4,
    'Semi-Private Full Day': 8,
};

const createInitialPricing = () => {
  const pricing = {};
  CRUISE_TYPES.forEach(type => {
    const key = type.replace(/\s+/g, '').replace(/-(\w)/g, (_, c) => c.toUpperCase()); // e.g., privateHalfDay
    pricing[key] = {
      lowSeason: { 
        fixedRate: '', 
        perGuestRate: '', 
        maxPrice: '',
        guestTiers: [] // For tiered pricing based on guest count
      },
      mediumSeason: { 
        fixedRate: '', 
        perGuestRate: '', 
        maxPrice: '',
        guestTiers: []
      },
      highSeason: { 
        fixedRate: '', 
        perGuestRate: '', 
        maxPrice: '',
        guestTiers: []
      },
    };
  });
  return pricing;
};

const CreateYachtModal = ({
  isOpen,
  onClose,
  onSave,
  isCreating
}) => {
  // State for form data, including nested objects
  const [formData, setFormData] = useState({
    stringId: '',
    name: '',
    builder: '',
    yachtType: YACHT_TYPES[0],
    guestCapacity: '',
    crewCapacity: '',
    yearBuilt: '',
    yearRefitted: '',
    specifications: { length: '', beam: '', draft: '' },
    cruiseOptions: CRUISE_TYPES.map(type => ({
        type,
        available: true, // Default to available
        durationHours: DEFAULT_DURATIONS[type] || '',
    })),
    // Use helper for initial pricing structure
    pricing: createInitialPricing(),
    includedItems: '', // Keep as string for textarea, parse on submit
    signatureMealDetails: '', // Keep as string
    extraServices: '', // Keep as string
    destinations: { primary: '', secondary: '' }, // Assuming simple structure for now
    totalUnits: 1,
    status: YACHT_STATUSES[0],
    description: '',
    yachtImages: [], // Use an array for multiple images
  });
  // Separate state for image previews
  const [imagePreviews, setImagePreviews] = useState([]);

  // Reset form when modal opens/closes or relevant props change
  useEffect(() => {
    if (isOpen) {
      setFormData({
        stringId: '',
        name: '',
        builder: '',
        yachtType: YACHT_TYPES[0],
        guestCapacity: '',
        crewCapacity: '',
        yearBuilt: '',
        yearRefitted: '',
        specifications: { length: '', beam: '', draft: '' },
        cruiseOptions: CRUISE_TYPES.map(type => ({
            type,
            available: true,
            durationHours: DEFAULT_DURATIONS[type] || '',
        })),
        pricing: createInitialPricing(),
        includedItems: '',
        signatureMealDetails: '',
        extraServices: '',
        destinations: { primary: '', secondary: '' },
        totalUnits: 1,
        status: YACHT_STATUSES[0],
        description: '',
        yachtImages: [],
      });
      setImagePreviews([]);
    }
  }, [isOpen]);


  if (!isOpen) return null;

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3 }
  };

  // Handle simple input changes
   const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle nested input changes (e.g., specifications, destinations)
  const handleNestedChange = (e, parentKey) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [name]: value
      }
    }));
  };

   // Handle pricing changes - updated for nested structure
   const handlePricingChange = (e, cruiseKey, seasonKey, fieldName) => {
     const { value } = e.target;
     setFormData(prev => {
       // Deep copy to avoid mutation issues
       const newPricing = JSON.parse(JSON.stringify(prev.pricing));
       if (!newPricing[cruiseKey]) {
         newPricing[cruiseKey] = {};
       }
       if (!newPricing[cruiseKey][seasonKey]) {
         newPricing[cruiseKey][seasonKey] = {};
       }
       newPricing[cruiseKey][seasonKey][fieldName] = value;

       return {
         ...prev,
         pricing: newPricing
       };
     });
   };

  // Add a new tier to the guest tiers array
  const handleAddGuestTier = (cruiseKey, seasonKey) => {
    setFormData(prev => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));
      if (!newPricing[cruiseKey]) {
        newPricing[cruiseKey] = {};
      }
      if (!newPricing[cruiseKey][seasonKey]) {
        newPricing[cruiseKey][seasonKey] = {};
      }
      if (!newPricing[cruiseKey][seasonKey].guestTiers) {
        newPricing[cruiseKey][seasonKey].guestTiers = [];
      }

      // Add a new tier
      newPricing[cruiseKey][seasonKey].guestTiers.push({
        guests: '',
        rate: ''
      });

      return {
        ...prev,
        pricing: newPricing
      };
    });
  };

  // Remove a tier from the guest tiers array
  const handleRemoveGuestTier = (cruiseKey, seasonKey, tierIndex) => {
    setFormData(prev => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));
      
      // Remove the tier at the specified index
      if (newPricing[cruiseKey]?.[seasonKey]?.guestTiers) {
        newPricing[cruiseKey][seasonKey].guestTiers.splice(tierIndex, 1);
      }

      return {
        ...prev,
        pricing: newPricing
      };
    });
  };

  // Update a specific tier's value (either guests or rate)
  const handleGuestTierChange = (cruiseKey, seasonKey, tierIndex, field, value) => {
    setFormData(prev => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));
      
      // Update the specific field in the tier
      if (newPricing[cruiseKey]?.[seasonKey]?.guestTiers?.[tierIndex]) {
        newPricing[cruiseKey][seasonKey].guestTiers[tierIndex][field] = value;
      }

      return {
        ...prev,
        pricing: newPricing
      };
    });
  };

  // Handle cruise option availability change
  const handleCruiseOptionChange = (index, checked) => {
    setFormData(prev => {
      const newCruiseOptions = [...prev.cruiseOptions];
      newCruiseOptions[index].available = checked;
      // Optionally clear duration if unchecked
      // if (!checked) {
      //   newCruiseOptions[index].durationHours = '';
      // }
      return { ...prev, cruiseOptions: newCruiseOptions };
    });
  };

  // Handle cruise duration change
  const handleCruiseDurationChange = (index, value) => {
    setFormData(prev => {
      const newCruiseOptions = [...prev.cruiseOptions];
      newCruiseOptions[index].durationHours = value;
      return { ...prev, cruiseOptions: newCruiseOptions };
    });
  };

  // Handle image file selection for multiple images
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      yachtImages: [...prev.yachtImages, ...files] // Append new files
    }));

    // Generate previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);

    // Clear the input value to allow selecting the same file again if needed
    e.target.value = null;
  };

  // Handle removing an image preview and the corresponding file
  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      yachtImages: prev.yachtImages.filter((_, index) => index !== indexToRemove)
    }));
    setImagePreviews(prev => {
      const newPreviews = prev.filter((_, index) => index !== indexToRemove);
      // Revoke the object URL of the removed preview
      URL.revokeObjectURL(prev[indexToRemove]);
      return newPreviews;
    });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert textarea strings to arrays
    const includedItemsArray = formData.includedItems.split('\\n').map(item => item.trim()).filter(item => item);
    const signatureMealDetailsArray = formData.signatureMealDetails.split('\\n').map(item => item.trim()).filter(item => item);
    const extraServicesArray = formData.extraServices.split('\\n').map(item => item.trim()).filter(item => item);

    // Create FormData object for multipart/form-data
    const dataToSubmit = new FormData();
    
    // Process yearBuilt and yearRefitted to ensure they're proper numbers or null
    const processedData = { ...formData };
    if (processedData.yearBuilt === '') {
      processedData.yearBuilt = null;
    } else if (processedData.yearBuilt) {
      processedData.yearBuilt = Number(processedData.yearBuilt);
    }
    
    if (processedData.yearRefitted === '') {
      processedData.yearRefitted = null;
    } else if (processedData.yearRefitted) {
      processedData.yearRefitted = Number(processedData.yearRefitted);
    }

    // Append simple fields
    Object.keys(processedData).forEach(key => {
      if (key !== 'pricing' && key !== 'specifications' && key !== 'destinations' && key !== 'cruiseOptions' && 
          key !== 'yachtImages' && key !== 'includedItems' && key !== 'signatureMealDetails' && key !== 'extraServices') {
        // Only send if not undefined/null 
        if (processedData[key] !== undefined && processedData[key] !== null) {
          dataToSubmit.append(key, processedData[key]);
        }
      }
    });

    // Append JSON stringified objects/arrays
    dataToSubmit.append('specifications', JSON.stringify(formData.specifications || {}));
    dataToSubmit.append('destinations', JSON.stringify(formData.destinations || {}));
    dataToSubmit.append('cruiseOptions', JSON.stringify(formData.cruiseOptions || []));
    dataToSubmit.append('pricing', JSON.stringify(formData.pricing || {})); // Send the nested pricing object
    dataToSubmit.append('includedItems', JSON.stringify(includedItemsArray || []));
    dataToSubmit.append('signatureMealDetails', JSON.stringify(signatureMealDetailsArray || []));
    dataToSubmit.append('extraServices', JSON.stringify(extraServicesArray || []));


    // Append image files
    formData.yachtImages.forEach((file) => {
      dataToSubmit.append('yachtImages', file); // Use 'yachtImages' matching backend upload.array()
    });

    // Log data being sent for debugging
    // console.log("Submitting FormData:");
    // for (let [key, value] of dataToSubmit.entries()) {
    //   console.log(key, value);
    // }

    onSave(dataToSubmit);
  };

  // Helper to generate cruise key (e.g., privateHalfDay)
  const getCruiseKey = (type) => type.replace(/\s+/g, '').replace(/-(\w)/g, (_, c) => c.toUpperCase());


  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl relative z-10 max-h-[95vh] flex flex-col"
          >
            {/* Header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-600 flex justify-between items-center rounded-t-xl">
              <div className="flex items-center space-x-3">
                <IoBoatOutline className="text-white text-2xl" />
                <h2 className="text-xl font-bold text-white">Add New Yacht</h2>
              </div>
              <button onClick={onClose} className="p-2 rounded-full text-gray-300 hover:bg-gray-600 hover:text-white transition-colors" aria-label="Close modal">
                <IoClose size={24} />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-grow">
              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Yacht Identity Section */}
                <motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><FaShip className="mr-2 text-blue-600"/>Yacht Identity</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">String ID *</label>
                      <input type="text" name="stringId" value={formData.stringId} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., azimut_60_fly" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Yacht Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., 'Azure Dream'" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Builder *</label>
                      <input type="text" name="builder" value={formData.builder} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., Azimut" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Yacht Type *</label>
                      <select name="yachtType" value={formData.yachtType} onChange={handleChange} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                        {YACHT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Year Built</label>
                      <input type="number" name="yearBuilt" value={formData.yearBuilt} onChange={handleChange} min="1900" max={new Date().getFullYear() + 1} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder={new Date().getFullYear().toString()} />
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Year Refitted</label>
                      <input type="number" name="yearRefitted" value={formData.yearRefitted} onChange={handleChange} min="1900" max={new Date().getFullYear() + 1} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="Optional"/>
                    </div>
                  </div>
                </motion.section>

                {/* Capacity & Specifications Section */}
                <motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><FaRulerCombined className="mr-2 text-purple-600"/>Capacity & Specifications</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Guest Capacity *</label>
                      <input type="number" name="guestCapacity" value={formData.guestCapacity} onChange={handleChange} required min="1" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Crew Capacity</label>
                      <input type="number" name="crewCapacity" value={formData.crewCapacity} onChange={handleChange} min="0" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Total Units</label>
                      <input type="number" name="totalUnits" value={formData.totalUnits} onChange={handleChange} min="0" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Length (e.g., 18m) *</label>
                      <input type="text" name="length" value={formData.specifications.length} onChange={(e) => handleNestedChange(e, 'specifications')} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Beam (e.g., 5m) *</label>
                      <input type="text" name="beam" value={formData.specifications.beam} onChange={(e) => handleNestedChange(e, 'specifications')} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Draft (e.g., 1.5m) *</label>
                      <input type="text" name="draft" value={formData.specifications.draft} onChange={(e) => handleNestedChange(e, 'specifications')} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
                    </div>
                  </div>
                </motion.section>

                 {/* Cruise Options & Pricing Section */}
<motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center"><FaMoneyBillWave className="mr-2 text-green-600"/>Cruise Options, Durations & Pricing</h3>

                    {/* Cruise Types & Durations */}
                    <div className="mb-8">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Available Cruise Types & Durations *</label>
                        <div className="space-y-4">
                            {formData.cruiseOptions.map((option, index) => (
                                <div key={option.type} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center p-3 border rounded-md hover:bg-gray-100 transition-colors">
                                    <label className="flex items-center space-x-3 cursor-pointer col-span-1 sm:col-span-1">
                                        <input
                                            type="checkbox"
                                            checked={option.available}
                                            onChange={(e) => handleCruiseOptionChange(index, e.target.checked)}
                                            className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                                        />
                                        <span className="text-sm font-medium text-gray-800">{option.type}</span>
                                    </label>
                                    <div className="col-span-1 sm:col-span-2">
                                        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                                            <FaClock className="mr-1 h-3 w-3 text-gray-500"/> Duration (Hours) *
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={option.durationHours ?? ''}
                                            onChange={(e) => handleCruiseDurationChange(index, e.target.value)}
                                            required={option.available}
                                            disabled={!option.available}
                                            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2 ${!option.available ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''}`}
                                            placeholder="e.g., 4"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        {formData.cruiseOptions.filter(o => o?.available).length === 0 && (
                            <p className="text-red-500 text-xs mt-2">At least one cruise type must be available and have a duration.</p>
                        )}
                    </div>

                    {/* Pricing Section */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Seasonal Pricing *</label>
                        <p className="text-xs text-gray-500 mb-4">
                            Define pricing for each available cruise type per season. You can use 'Fixed Rate' OR 'Per Guest Rate' (with an optional 'Max Price' cap). If both are set, 'Per Guest Rate' takes priority.
                        </p>
                        <div className="space-y-6">
                            {formData.cruiseOptions.filter(o => o.available).map(option => {
                                const cruiseKey = getCruiseKey(option.type); // e.g., privateHalfDay
                                return (
                                    <div key={cruiseKey} className="p-4 border rounded-md bg-white">
                                        <h4 className="text-md font-semibold text-gray-700 mb-4">{option.type}</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                                            {/* Headers */}
                                            <div className="font-medium text-sm text-gray-500">Season</div>
                                            <div className="font-medium text-sm text-gray-500 text-center">Low (Apr, May, Sep, Oct)</div>
                                            <div className="font-medium text-sm text-gray-500 text-center">High (Jun, Jul, Aug)</div>
                                            {/* Medium Season Placeholder - Add if needed */}
                                            {/* <div className="font-medium text-sm text-gray-500 text-center">Medium</div> */}

                                            {/* Fixed Rate Row */}
                                            <div className="text-sm self-center flex items-center"><FaEuroSign className="mr-1 h-3 w-3 text-gray-500"/> Fixed Rate</div>
                                            {['lowSeason', 'highSeason'].map(seasonKey => ( // Add 'mediumSeason' if used
                                                <input
                                                    key={`${cruiseKey}-${seasonKey}-fixed`}
                                                    type="number" min="0" step="any"
                                                    value={formData.pricing[cruiseKey]?.[seasonKey]?.fixedRate ?? ''}
                                                    onChange={(e) => handlePricingChange(e, cruiseKey, seasonKey, 'fixedRate')}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2"
                                                    placeholder="e.g., 1000"
                                                />
                                            ))}                                            {/* Per Guest Rate Row */}
                                            <div className="text-sm self-center flex items-center"><FaUserFriends className="mr-1 h-3 w-3 text-gray-500"/> Per Guest Rate</div>
                                            {['lowSeason', 'highSeason'].map(seasonKey => ( // Add 'mediumSeason' if used
                                                <input
                                                    key={`${cruiseKey}-${seasonKey}-perGuest`}
                                                    type="number" min="0" step="any"
                                                    value={formData.pricing[cruiseKey]?.[seasonKey]?.perGuestRate ?? ''}
                                                    onChange={(e) => handlePricingChange(e, cruiseKey, seasonKey, 'perGuestRate')}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2"
                                                    placeholder="e.g., 150"
                                                />
                                            ))}

                                            {/* Max Price Row */}
                                            <div className="text-sm self-center flex items-center"><FaBan className="mr-1 h-3 w-3 text-gray-500"/> Max Price (Cap)</div>
                                            {['lowSeason', 'highSeason'].map(seasonKey => ( // Add 'mediumSeason' if used
                                                <input
                                                    key={`${cruiseKey}-${seasonKey}-max`}
                                                    type="number" min="0" step="any"
                                                    value={formData.pricing[cruiseKey]?.[seasonKey]?.maxPrice ?? ''}
                                                    onChange={(e) => handlePricingChange(e, cruiseKey, seasonKey, 'maxPrice')}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2"
                                                    placeholder="Optional cap"
                                                />
                                            ))}
                                            
                                            {/* Guest Tier Pricing Section */}
                                            <div className="col-span-3 mt-4">
                                                <div className="border-t pt-4 mb-2">
                                                    <div className="flex justify-between items-center">
                                                        <h5 className="text-sm font-medium text-gray-700">Guest Tier Pricing</h5>
                                                        <p className="text-xs text-gray-500">For different prices based on number of guests</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                                    {/* Low Season Tiers Column */}
                                                    <div className="border rounded-md p-3">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h6 className="text-sm font-medium text-gray-700">Low Season Tiers</h6>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddGuestTier(cruiseKey, 'lowSeason')}
                                                                className="px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs hover:bg-blue-100"
                                                            >
                                                                + Add Tier
                                                            </button>
                                                        </div>
                                                        {/* Low Season Tier Items */}
                                                        {formData.pricing[cruiseKey]?.lowSeason?.guestTiers?.map((tier, tierIndex) => (
                                                            <div key={tierIndex} className="flex space-x-2 items-center mb-2">
                                                                <div className="flex-1">
                                                                    <input
                                                                        type="text"
                                                                        value={tier.guests}
                                                                        onChange={(e) => handleGuestTierChange(cruiseKey, 'lowSeason', tierIndex, 'guests', e.target.value)}
                                                                        placeholder="e.g., 1-2 or 3+"
                                                                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="relative rounded-md shadow-sm">
                                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                                                            <span className="text-gray-500 sm:text-sm">€</span>
                                                                        </div>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={tier.rate}
                                                                            onChange={(e) => handleGuestTierChange(cruiseKey, 'lowSeason', tierIndex, 'rate', e.target.value)}
                                                                            placeholder="Price"
                                                                            className="w-full pl-6 text-sm border-gray-300 rounded-md shadow-sm"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveGuestTier(cruiseKey, 'lowSeason', tierIndex)}
                                                                    className="flex-none p-1 text-red-500 hover:text-red-700"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {(!formData.pricing[cruiseKey]?.lowSeason?.guestTiers || formData.pricing[cruiseKey]?.lowSeason?.guestTiers.length === 0) && (
                                                            <p className="text-xs text-gray-500 italic">No tiers added. Click "Add Tier" to create guest-based pricing.</p>
                                                        )}
                                                    </div>
                                                    
                                                    {/* High Season Tiers Column */}
                                                    <div className="border rounded-md p-3">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h6 className="text-sm font-medium text-gray-700">High Season Tiers</h6>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddGuestTier(cruiseKey, 'highSeason')}
                                                                className="px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs hover:bg-blue-100"
                                                            >
                                                                + Add Tier
                                                            </button>
                                                        </div>
                                                        {/* High Season Tier Items */}
                                                        {formData.pricing[cruiseKey]?.highSeason?.guestTiers?.map((tier, tierIndex) => (
                                                            <div key={tierIndex} className="flex space-x-2 items-center mb-2">
                                                                <div className="flex-1">
                                                                    <input
                                                                        type="text"
                                                                        value={tier.guests}
                                                                        onChange={(e) => handleGuestTierChange(cruiseKey, 'highSeason', tierIndex, 'guests', e.target.value)}
                                                                        placeholder="e.g., 1-2 or 3+"
                                                                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <div className="relative rounded-md shadow-sm">
                                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                                                            <span className="text-gray-500 sm:text-sm">€</span>
                                                                        </div>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            value={tier.rate}
                                                                            onChange={(e) => handleGuestTierChange(cruiseKey, 'highSeason', tierIndex, 'rate', e.target.value)}
                                                                            placeholder="Price"
                                                                            className="w-full pl-6 text-sm border-gray-300 rounded-md shadow-sm"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveGuestTier(cruiseKey, 'highSeason', tierIndex)}
                                                                    className="flex-none p-1 text-red-500 hover:text-red-700"
                                                                >
                                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        ))}
                                                        {(!formData.pricing[cruiseKey]?.highSeason?.guestTiers || formData.pricing[cruiseKey]?.highSeason?.guestTiers.length === 0) && (
                                                            <p className="text-xs text-gray-500 italic">No tiers added. Click "Add Tier" to create guest-based pricing.</p>
                                                        )}
                                                    </div>
                                                </div>                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                             {/* Validation message if no pricing is entered for an available cruise */}
                             {formData.cruiseOptions.some(opt => opt.available && !Object.values(formData.pricing[getCruiseKey(opt.type)] || {}).some(season => season.fixedRate || season.perGuestRate)) && (
                                <p className="text-red-500 text-xs mt-2">Pricing must be defined (Fixed or Per Guest) for all available cruise types in at least one season.</p>
                             )}
                        </div>
                    </div>
                </motion.section>

                {/* Features & Description Section */}
                <motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><FaCheckCircle className="mr-2 text-teal-600"/>Features & Description</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Included Items (one per line)</label>
                      <textarea name="includedItems" value={formData.includedItems} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., Welcome Drink&#10;Snacks&#10;Towels"></textarea>
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Signature Meal Details (one per line)</label>
                      <textarea name="signatureMealDetails" value={formData.signatureMealDetails} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., Grilled Fish&#10;Greek Salad&#10;Local Wine"></textarea>
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Extra Services (one per line)</label>
                      <textarea name="extraServices" value={formData.extraServices} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="e.g., DJ Set - €500&#10;Premium Bar - €300"></textarea>
                    </div>
                     <div>
                      <label className="block text-sm font-medium text-gray-700">Description</label>
                      <textarea name="description" value={formData.description || ''} onChange={handleChange} rows="4" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" placeholder="Detailed description of the yacht..."></textarea>
                    </div>
                  </div>
                </motion.section>

{/* Yacht Images */}
                <motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><FaImage className="mr-2 text-orange-600"/>Yacht Images</h3>
                    {/* Image Previews */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        {imagePreviews.map((previewUrl, index) => (
                            <div key={index} className="relative group">
                                <img src={previewUrl} alt={`Preview ${index + 1}`} className="h-24 w-24 object-cover rounded-md border border-gray-300"/>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveImage(index)}
                                    className="absolute top-0 right-0 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Remove image"
                                >
                                    <IoClose size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                    {/* File Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Add Images *</label>
                        <input
                            type="file"
                            name="yachtImages"
                            accept="image/*"
                            multiple
                            onChange={handleImageChange}
                            required={formData.yachtImages.length === 0} // Require at least one image on create
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="mt-1 text-xs text-gray-500">Upload one or more images (JPG, PNG, WEBP, GIF). Max 10MB per file.</p>
                         {formData.yachtImages.length === 0 && (
                            <p className="text-red-500 text-xs mt-1">At least one image is required.</p>
                         )}
                    </div>
                </motion.section>

                {/* Status */}
                <motion.section {...fadeInUp} className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center"><FaCog className="mr-2 text-gray-600"/>Status</h3>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Yacht Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full md:w-1/3 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                            {YACHT_STATUSES.map(status => <option key={status} value={status} className="capitalize">{status}</option>)}
                        </select>
                    </div>
                </motion.section>


                {/* Actions */}
                <motion.div {...fadeInUp} className="sticky bottom-0 bg-white pt-5 pb-5 border-t border-gray-200 px-6 -mx-6 -mb-6 rounded-b-xl">
                  <div className="flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                    >
                      {isCreating ? (
                        <>
                          <LoadingSpinner size="sm" color="text-white" />
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <IoBoatOutline />
                          <span>Create Yacht</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CreateYachtModal;
